import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ArrowUpRight,
  Award,
  BookMarked,
  BookOpen,
  CalendarClock,
  Crown,
  Flame,
  Globe,
  GraduationCap,
  Grid3x3,
  Library,
  Loader2,
  Medal,
  Minus,
  Plus,
  Sparkles,
  Star,
  Sun,
  Trophy,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Kicker, SmchsLayout } from '@/components/smchs/Shell';
import { TokenManager } from '@/lib/token-manager';
import {
  Challenge,
  ChallengeIcon,
  ChallengeTint,
  ChallengeWinner,
  FormLeaderboardEntry,
  Milestone,
  ReaderLeaderboardEntry,
  challengesApi,
  challengesHallOfFameApi,
  challengesLeaderboardApi,
  challengesMilestonesApi,
  challengesReadLogsApi,
} from '@/services/challenges-api';
import { Book, booksApi } from '@/services/catalog-api';
import { authorLine } from '@/components/smchs/collection-lib';

/* ===================================================================== *
 *  Challenges: wired to /api/v1/challenges/ endpoints.
 * ===================================================================== */

const ICON_MAP: Record<ChallengeIcon, React.ElementType> = {
  trophy: Trophy,
  globe: Globe,
  grid: Grid3x3,
  sun: Sun,
  star: Star,
  book: BookOpen,
};

const TINT_CLASS_MAP: Record<ChallengeTint, string> = {
  navy: 'challenge-tint-navy',
  gold: 'challenge-tint-gold',
  green: 'challenge-tint-green',
  purple: 'challenge-tint-purple',
};

const MILESTONE_ICONS: Record<number, React.ElementType> = {
  5: BookOpen,
  15: BookMarked,
  30: Library,
  50: GraduationCap,
  100: Sparkles,
};

function isAuthed(): boolean {
  return TokenManager.isTokenValid();
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function minReadDateIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

/* =============================================================== */
/*  Page                                                            */
/* =============================================================== */

type LoadStatus = 'idle' | 'loading' | 'ok' | 'error';

const ChallengesPage: React.FC = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [challengesStatus, setChallengesStatus] = useState<LoadStatus>('idle');
  const [forms, setForms] = useState<FormLeaderboardEntry[]>([]);
  const [formsStatus, setFormsStatus] = useState<LoadStatus>('idle');
  const [readers, setReaders] = useState<ReaderLeaderboardEntry[]>([]);
  const [readersStatus, setReadersStatus] = useState<LoadStatus>('idle');
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [winners, setWinners] = useState<ChallengeWinner[]>([]);
  const [logOpen, setLogOpen] = useState(false);
  const [joiningSlug, setJoiningSlug] = useState<string | null>(null);

  const primary = useMemo(() => challenges.find((c) => c.is_primary), [challenges]);

  const refetchChallenges = useCallback(() => {
    if (!isAuthed()) return;
    setChallengesStatus('loading');
    challengesApi
      .list()
      .then((cs) => { setChallenges(cs); setChallengesStatus('ok'); })
      .catch(() => setChallengesStatus('error'));
  }, []);

  useEffect(() => {
    if (!isAuthed()) {
      setChallengesStatus('idle');
      return;
    }
    refetchChallenges();
    challengesMilestonesApi.list().then(setMilestones).catch(() => null);
    challengesHallOfFameApi
      .list()
      .then((r) => setWinners(r.results))
      .catch(() => null);
  }, [refetchChallenges]);

  useEffect(() => {
    if (!isAuthed() || !primary) {
      setForms([]); setReaders([]);
      setFormsStatus('idle'); setReadersStatus('idle');
      return;
    }
    setFormsStatus('loading');
    challengesLeaderboardApi
      .getForms(primary.slug)
      .then((d) => { setForms(d); setFormsStatus('ok'); })
      .catch(() => setFormsStatus('error'));
    setReadersStatus('loading');
    challengesLeaderboardApi
      .getReaders(primary.slug)
      .then((d) => { setReaders(d); setReadersStatus('ok'); })
      .catch(() => setReadersStatus('error'));
  }, [primary]);

  function requireAuth(then: () => void) {
    if (isAuthed()) then();
    else navigate('/signin');
  }

  async function handleJoin(c: Challenge) {
    requireAuth(async () => {
      if (joiningSlug) return;
      setJoiningSlug(c.slug);
      try {
        await challengesApi.join(c.slug);
        toast.success(`Joined "${c.name}"`);
        setChallenges((prev) =>
          prev.map((x) =>
            x.slug === c.slug
              ? { ...x, is_enrolled: true, participants: x.participants + (x.is_enrolled ? 0 : 1) }
              : x,
          ),
        );
      } catch {
        /* response interceptor toasts */
      } finally {
        setJoiningSlug(null);
      }
    });
  }

  async function handleLeave(c: Challenge) {
    requireAuth(async () => {
      if (joiningSlug) return;
      setJoiningSlug(c.slug);
      try {
        await challengesApi.leave(c.slug);
        toast.success(`Left "${c.name}"`);
        setChallenges((prev) =>
          prev.map((x) =>
            x.slug === c.slug
              ? { ...x, is_enrolled: false, participants: Math.max(0, x.participants - 1) }
              : x,
          ),
        );
      } catch {
        /* interceptor */
      } finally {
        setJoiningSlug(null);
      }
    });
  }

  const enrolledIn = challenges.filter((c) => c.is_enrolled);
  const enrolledHasGenreUnit = enrolledIn.some((c) => c.unit === 'genres');

  return (
    <SmchsLayout>
      <ChallengesStyleBlock />
      <div className="smchs-challenges">
        <Hero
          showLogButton={enrolledIn.length > 0}
          onLogClick={() => requireAuth(() => setLogOpen(true))}
        />
        {primary && <LiveProgress challenge={primary} />}
        <ActiveChallenges
          challenges={challenges}
          status={challengesStatus}
          onRetry={refetchChallenges}
          joiningSlug={joiningSlug}
          onJoin={handleJoin}
          onLeave={handleLeave}
        />
        <Leaderboard
          forms={forms}
          readers={readers}
          hasPrimary={!!primary}
          formsStatus={formsStatus}
          readersStatus={readersStatus}
        />
        <Milestones milestones={milestones} />
        <HallOfFame winners={winners} />
        <JoinCTA />

        <LogReadModal
          open={logOpen}
          preferCatalog={enrolledHasGenreUnit}
          onClose={() => setLogOpen(false)}
          onLogged={() => {
            setLogOpen(false);
            refetchChallenges();
            if (primary) {
              challengesLeaderboardApi
                .getForms(primary.slug)
                .then(setForms)
                .catch(() => null);
              challengesLeaderboardApi
                .getReaders(primary.slug)
                .then(setReaders)
                .catch(() => null);
            }
          }}
        />
      </div>
    </SmchsLayout>
  );
};

const ChallengesStyleBlock: React.FC = () => (
  <style>{`
    .smchs-challenges {
      --challenge-gold-button-ink: var(--nav-bg);
      --challenge-cta-shadow: 0 4px 14px -4px color-mix(in srgb, var(--gold) 72%, transparent);
      --challenge-hero-glow: color-mix(in srgb, var(--gold) 24%, transparent);
      --challenge-medal-silver: color-mix(in srgb, var(--ink-soft) 46%, var(--surface));
      --challenge-medal-bronze: color-mix(in srgb, var(--gold) 64%, var(--ink));
      --challenge-medal-silver-bg: color-mix(in srgb, var(--ink-soft) 18%, transparent);
      --challenge-medal-bronze-bg: color-mix(in srgb, var(--gold) 22%, transparent);
    }

    .smchs-challenges .challenge-tint-navy {
      --challenge-tint-from: var(--primary);
      --challenge-tint-to: var(--ink);
    }

    .smchs-challenges .challenge-tint-gold {
      --challenge-tint-from: var(--gold);
      --challenge-tint-to: color-mix(in srgb, var(--primary) 78%, var(--ink));
    }

    .smchs-challenges .challenge-tint-green {
      --challenge-tint-from: color-mix(in srgb, var(--primary) 62%, var(--gold));
      --challenge-tint-to: color-mix(in srgb, var(--ink) 76%, var(--primary));
    }

    .smchs-challenges .challenge-tint-purple {
      --challenge-tint-from: color-mix(in srgb, var(--primary) 84%, var(--gold));
      --challenge-tint-to: color-mix(in srgb, var(--ink) 82%, var(--primary));
    }
  `}</style>
);

/* =============================================================== */
/*  HERO                                                            */
/* =============================================================== */

const Hero: React.FC<{ showLogButton: boolean; onLogClick: () => void }> = ({
  showLogButton,
  onLogClick,
}) => (
  <section className="relative overflow-hidden border-b border-[var(--border)]">
    <div className="absolute inset-0 -z-10">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,var(--challenge-hero-glow),transparent_60%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_60%_80%_at_50%_0%,black,transparent_70%)] opacity-40" />
    </div>

    <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-12 md:pb-16">
      <Kicker label="The Challenge" />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="col-span-1 lg:col-span-8 font-display font-[900] text-[2.75rem] sm:text-[3.75rem] md:text-[5rem] leading-[0.95] tracking-[-0.03em] text-[var(--ink)]"
        >
          Read more.
          <span className="block">
            Read <span className="font-serif italic font-normal text-[var(--primary)]">wider</span>.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="col-span-1 lg:col-span-4 text-[var(--ink-soft)] text-[1rem] leading-[1.55]"
        >
          Termly goals, genre marathons and a leaderboard for every form.
          Every book you log counts toward something.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-10 flex flex-wrap items-center gap-3"
      >
        <a
          href="#challenges"
          className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] px-5 py-3 rounded-xl font-medium text-[14px] hover:bg-[var(--primary)] transition-colors"
        >
          Join the challenge <ArrowRight className="w-4 h-4" />
        </a>
        <a
          href="#leaderboard"
          className="inline-flex items-center gap-2 border border-[var(--border)] hover:border-[var(--ink)]/40 text-[var(--ink)] px-5 py-3 rounded-xl font-medium text-[14px] transition-colors bg-[var(--surface)]"
        >
          <Trophy className="w-4 h-4" /> See leaderboard
        </a>
        {showLogButton && (
          <button
            onClick={onLogClick}
            className="inline-flex items-center gap-2 bg-[var(--gold)] text-[var(--challenge-gold-button-ink)] px-5 py-3 rounded-xl font-semibold text-[14px] hover:brightness-110 transition-all"
          >
            <Plus className="w-4 h-4" /> Log a read
          </button>
        )}
      </motion.div>
    </div>
  </section>
);

/* =============================================================== */
/*  LIVE PROGRESS: big school-wide counter                         */
/* =============================================================== */

const LiveProgress: React.FC<{ challenge: Challenge }> = ({ challenge }) => {
  const read = challenge.progress;
  const goal = challenge.target;
  const pct = goal > 0 ? (read / goal) * 100 : 0;
  const remaining = Math.max(0, goal - read);
  return (
    <section className="border-b border-[var(--border)] bg-[var(--surface-2)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-end">
          <div className="col-span-1 lg:col-span-7">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)] mb-3 inline-flex items-center gap-2">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[var(--gold)] opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[var(--gold)]" />
              </span>
              School progress · live
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="font-display font-[900] text-[5rem] sm:text-[6.5rem] md:text-[8rem] leading-[0.85] tracking-[-0.04em] text-[var(--ink)]">
                {read.toLocaleString()}
              </div>
              <div className="font-serif italic text-[1.5rem] md:text-[2rem] text-[var(--ink-soft)]">
                of {goal.toLocaleString()} {challenge.unit}
              </div>
            </div>

            <div className="mt-6 h-3 bg-[var(--surface)] border border-[var(--border)] rounded-full overflow-hidden">
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: Math.min(1, pct / 100) }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-[var(--gold)] to-[var(--gold-soft)] origin-left rounded-full"
              />
            </div>

            <div className="mt-3 flex items-center justify-between text-[12px] font-mono uppercase tracking-wider text-[var(--muted)]">
              <span>{pct.toFixed(1)}% complete</span>
              <span>{remaining} to go</span>
            </div>
          </div>

          <div className="col-span-1 lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[var(--surface)] border border-[var(--border)] p-5">
              <div className="font-display font-[900] text-[2.5rem] leading-none tracking-tight">
                {challenge.days_left}
              </div>
              <div className="mt-2 text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
                days left
              </div>
            </div>
            <div className="rounded-2xl bg-[var(--surface)] border border-[var(--border)] p-5">
              <div className="font-display font-[900] text-[2.5rem] leading-none tracking-tight">
                {challenge.participants}
              </div>
              <div className="mt-2 text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
                students in
              </div>
            </div>
            <div className="col-span-2 rounded-2xl bg-[var(--ink)] text-[var(--paper)] p-5">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--paper)]/60 inline-flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-[var(--gold)]" /> The prize
              </div>
              <p className="mt-2 font-serif italic text-[1.05rem] leading-[1.4]">
                {challenge.reward}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =============================================================== */
/*  ACTIVE CHALLENGES                                               */
/* =============================================================== */

const ActiveChallenges: React.FC<{
  challenges: Challenge[];
  status: LoadStatus;
  onRetry: () => void;
  joiningSlug: string | null;
  onJoin: (c: Challenge) => void;
  onLeave: (c: Challenge) => void;
}> = ({ challenges, status, onRetry, joiningSlug, onJoin, onLeave }) => {
  const featured = challenges.find((c) => c.is_primary) ?? challenges[0];
  const rest = challenges.filter((c) => c !== featured);

  return (
    <section id="challenges" className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <Kicker label="Active challenges" />
          <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[24ch]">
            Pick a goal that{' '}
            fits you.
          </h2>
        </div>
      </div>

      {status === 'error' && challenges.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="Couldn't load challenges"
          body="The catalog server didn't respond. Try again."
          actionLabel="Retry"
          onAction={onRetry}
        />
      ) : challenges.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="No challenges yet"
          body={
            isAuthed()
              ? "The librarian hasn't published any reading challenges. Check back soon."
              : 'Sign in to see live reading challenges, leaderboards, and the hall of fame.'
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-min">
          {featured && (
            <ChallengeCard
              challenge={featured}
              className="lg:col-span-7 lg:row-span-2"
              featured
              joiningSlug={joiningSlug}
              onJoin={onJoin}
              onLeave={onLeave}
            />
          )}
          {rest.slice(0, 2).map((c) => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              className="lg:col-span-5"
              joiningSlug={joiningSlug}
              onJoin={onJoin}
              onLeave={onLeave}
            />
          ))}
          {rest.slice(2).map((c) => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              className="lg:col-span-12"
              wide
              joiningSlug={joiningSlug}
              onJoin={onJoin}
              onLeave={onLeave}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const ChallengeCard: React.FC<{
  challenge: Challenge;
  className?: string;
  featured?: boolean;
  wide?: boolean;
  joiningSlug: string | null;
  onJoin: (c: Challenge) => void;
  onLeave: (c: Challenge) => void;
}> = ({ challenge: c, className = '', featured = false, wide = false, joiningSlug, onJoin, onLeave }) => {
  const pct = c.target > 0 ? (c.progress / c.target) * 100 : 0;
  const Icon = ICON_MAP[c.icon_slug] ?? Trophy;
  const tintClass = TINT_CLASS_MAP[c.tint_slug] ?? TINT_CLASS_MAP.navy;
  const upcoming = c.status === 'upcoming';
  const completed = c.status === 'completed';
  const closed = upcoming || completed;
  const isBusy = joiningSlug === c.slug;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (closed) return;
    if (c.is_enrolled) onLeave(c);
    else onJoin(c);
  };

  let actionLabel: string;
  if (upcoming) actionLabel = 'Notify me';
  else if (completed) actionLabel = c.is_enrolled ? 'Participated' : 'Closed';
  else if (c.is_enrolled) actionLabel = 'Leave';
  else actionLabel = 'Join';

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -3 }}
      className={`relative rounded-2xl border overflow-hidden transition-all cursor-pointer ${
        featured
          ? `${tintClass} bg-[linear-gradient(135deg,var(--challenge-tint-from),var(--challenge-tint-to))] text-white border-transparent`
          : 'bg-[var(--surface)] border-[var(--border)] hover:border-[var(--ink)]/30'
      } ${className}`}
    >
      {featured && (
        <>
          <div className="absolute inset-0 opacity-[0.10] bg-[radial-gradient(circle_at_25%_15%,white,transparent_60%)]" />
          <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full border border-white/10" />
          <div className="absolute -right-10 -bottom-10 w-52 h-52 rounded-full border border-white/10" />
        </>
      )}

      <div className={`relative ${featured ? 'p-7 md:p-9' : 'p-6'} flex flex-col h-full`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-5 gap-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                featured
                  ? 'bg-white/10 text-[var(--gold-soft)] ring-1 ring-white/20'
                  : 'bg-[var(--ink)] text-[var(--paper)]'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              {(upcoming || completed) && (
                <span
                  className={`inline-block text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded mb-1 ${
                    featured ? 'bg-white/15 text-white' : 'bg-[var(--gold)]/15 text-[var(--ink-soft)]'
                  }`}
                >
                  {upcoming ? 'Upcoming' : 'Closed'}
                </span>
              )}
              <div
                className={`font-mono uppercase text-[10px] tracking-[0.2em] ${
                  featured ? 'text-white/60' : 'text-[var(--muted)]'
                }`}
              >
                Challenge
              </div>
            </div>
          </div>
          {featured && (
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[var(--gold)]/20 text-[var(--gold-soft)] ring-1 ring-[var(--gold)]/30">
              Primary
            </span>
          )}
        </div>

        {/* Title + desc */}
        <h3
          className={`font-display font-bold tracking-tight leading-[1.05] ${
            featured ? 'text-[2rem] md:text-[2.5rem]' : 'text-[1.4rem]'
          }`}
        >
          {c.name}
        </h3>
        <p
          className={`mt-2.5 font-serif italic ${
            featured ? 'text-[1.05rem] text-white/85' : 'text-[14px] text-[var(--ink-soft)]'
          } leading-[1.5]`}
        >
          {c.desc}
        </p>

        {/* Progress */}
        <div className={`${wide ? 'mt-6' : 'mt-auto pt-7'}`}>
          <div className="flex items-baseline justify-between mb-2">
            <span className={`font-display font-[900] tracking-tight leading-none ${featured ? 'text-[2.5rem]' : 'text-[1.75rem]'}`}>
              {c.progress}
              <span className={`font-serif italic font-normal ${featured ? 'text-white/55 text-[1rem]' : 'text-[var(--muted)] text-[0.85rem]'} ml-2`}>
                of {c.target} {c.unit}
              </span>
            </span>
            <span
              className={`font-mono text-[12px] uppercase tracking-wider ${
                featured ? 'text-white/65' : 'text-[var(--muted)]'
              }`}
            >
              {pct.toFixed(0)}%
            </span>
          </div>
          <div
            className={`h-2 rounded-full overflow-hidden ${
              featured ? 'bg-white/15' : 'bg-[var(--surface-2)]'
            }`}
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: Math.min(1, pct / 100) }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
              className={`h-full origin-left rounded-full ${
                featured ? 'bg-[var(--gold)]' : 'bg-[var(--primary)]'
              }`}
            />
          </div>
          {c.is_enrolled && c.my_progress !== null && (
            <div
              className={`mt-2 text-[11px] font-mono uppercase tracking-wider ${
                featured ? 'text-white/70' : 'text-[var(--ink-soft)]'
              }`}
            >
              You: {c.my_progress} {c.unit}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`mt-6 pt-5 border-t flex flex-wrap items-center justify-between gap-4 ${
            featured ? 'border-white/15' : 'border-[var(--border)]'
          }`}
        >
          <div className={`text-[12px] ${featured ? 'text-white/75' : 'text-[var(--ink-soft)]'} flex items-center gap-4 flex-wrap`}>
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" />
              {c.days_left} days left
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              {c.participants} in
            </span>
          </div>
          <button
            onClick={handleClick}
            disabled={closed || isBusy}
            className={`text-[12px] font-medium uppercase tracking-wider font-mono inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
              featured
                ? 'text-[var(--gold-soft)] hover:text-[var(--gold)]'
                : 'text-[var(--ink-soft)] hover:text-[var(--ink)]'
            } transition-colors`}
          >
            {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {actionLabel}
            {!isBusy && !closed && <ArrowUpRight className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Reward */}
        {c.reward && (
          <div
            className={`mt-4 pt-4 border-t ${featured ? 'border-white/15' : 'border-[var(--border)]'} text-[12px] flex items-start gap-2 ${
              featured ? 'text-white/70' : 'text-[var(--muted)]'
            }`}
          >
            <Award className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${featured ? 'text-[var(--gold-soft)]' : 'text-[var(--gold)]'}`} />
            <span>{c.reward}</span>
          </div>
        )}
      </div>
    </motion.article>
  );
};

/* =============================================================== */
/*  LEADERBOARD                                                     */
/* =============================================================== */

const Leaderboard: React.FC<{
  forms: FormLeaderboardEntry[];
  readers: ReaderLeaderboardEntry[];
  hasPrimary: boolean;
  formsStatus: LoadStatus;
  readersStatus: LoadStatus;
}> = ({ forms, readers, hasPrimary, formsStatus, readersStatus }) => {
  const [tab, setTab] = useState<'forms' | 'readers'>('forms');

  return (
    <section
      id="leaderboard"
      className="border-y border-[var(--border)] bg-[var(--surface-2)]"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-24 pb-20">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <Kicker label="Leaderboard" />
            <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[24ch]">
              Who's{' '}
              winning{' '}
              this term.
            </h2>
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
            {(['forms', 'readers'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative px-5 py-2 text-[13px] font-medium rounded-lg transition-colors capitalize ${
                  tab === t ? 'text-[var(--paper)]' : 'text-[var(--ink-soft)] hover:text-[var(--ink)]'
                }`}
              >
                {tab === t && (
                  <motion.div
                    layoutId="lbTab"
                    className="absolute inset-0 bg-[var(--ink)] rounded-lg"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative">{t === 'forms' ? 'Top forms' : 'Top readers'}</span>
              </button>
            ))}
          </div>
        </div>

        {!hasPrimary ? (
          <EmptyState
            icon={Trophy}
            title="No leaderboard yet"
            body="The library has not opened a leaderboard yet. Check back soon."
          />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {tab === 'forms'
                ? <FormsBoard forms={forms} status={formsStatus} />
                : <ReadersBoard readers={readers} status={readersStatus} />}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </section>
  );
};

const Podium: React.FC<{ top3: FormLeaderboardEntry[] }> = ({ top3 }) => {
  if (top3.length < 3) return null;
  const ordered = [top3[1], top3[0], top3[2]];
  const heights = ['h-32', 'h-44', 'h-24'];
  const medals = [
    { Icon: Medal, color: 'text-[var(--challenge-medal-silver)]' },
    { Icon: Crown, color: 'text-[var(--gold)]' },
    { Icon: Medal, color: 'text-[var(--challenge-medal-bronze)]' },
  ];
  return (
    <div className="grid grid-cols-3 gap-3 mb-6 items-end">
      {ordered.map((f, i) => {
        const realRank = i === 1 ? 1 : i === 0 ? 2 : 3;
        const { Icon, color } = medals[realRank - 1];
        return (
          <motion.div
            key={f.form_name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
            className="text-center"
          >
            <div className="mb-3 flex flex-col items-center">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  realRank === 1
                    ? 'bg-[var(--gold)]/20 ring-2 ring-[var(--gold)]/50'
                    : 'bg-[var(--surface)] ring-1 ring-[var(--border)]'
                }`}
              >
                <Icon className={`w-7 h-7 ${color}`} strokeWidth={1.75} />
              </div>
              <div className="font-display font-bold text-[1.25rem] mt-3 tracking-tight">{f.form_name}</div>
              <div className="text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
                {f.books} books · avg {f.avg}
              </div>
            </div>
            <div
              className={`${heights[i]} rounded-t-xl ${
                realRank === 1
                  ? 'bg-gradient-to-b from-[var(--gold)] to-[var(--gold-soft)]'
                  : 'bg-[var(--surface)] border border-[var(--border)] border-b-0'
              } flex items-start justify-center pt-4`}
            >
              <span className="font-display font-[900] text-[2rem] leading-none text-[var(--ink)]">
                {realRank}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

const FormsBoard: React.FC<{ forms: FormLeaderboardEntry[]; status: LoadStatus }> = ({ forms, status }) => {
  if (status === 'error' && forms.length === 0) {
    return <EmptyState icon={Trophy} title="Couldn't load forms" body="The leaderboard failed to fetch. Refresh to try again." />;
  }
  if (forms.length === 0) {
    return <EmptyState icon={Trophy} title="No forms yet" body="Form rankings appear once students from a form start logging reads." />;
  }
  const top3 = forms.slice(0, 3);
  const rest = forms.slice(3);
  const max = forms[0].books || 1;
  return (
    <div>
      <Podium top3={top3} />
      {rest.length > 0 && (
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
          {rest.map((f, i) => (
            <RankRow key={f.form_name} f={f} max={max} delay={i * 0.04} />
          ))}
        </div>
      )}
    </div>
  );
};

const RankRow: React.FC<{ f: FormLeaderboardEntry; max: number; delay: number }> = ({ f, max, delay }) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay }}
    className="grid grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4 border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface-2)] transition-colors"
  >
    <div className="col-span-1 font-mono text-[14px] font-semibold text-[var(--muted)]">
      #{f.rank}
    </div>
    <div className="col-span-3 md:col-span-2 font-display font-bold text-[1.05rem] tracking-tight">
      {f.form_name}
    </div>
    <div className="col-span-5 md:col-span-6">
      <div className="h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: Math.min(1, f.books / max) }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: delay + 0.1, ease: 'easeOut' }}
          className="h-full bg-[var(--primary)] origin-left rounded-full"
        />
      </div>
    </div>
    <div className="col-span-2 text-right md:text-left text-[13px] font-mono">
      <span className="text-[var(--ink)] font-semibold">{f.books}</span>{' '}
      <span className="text-[var(--muted)]">/ avg {f.avg}</span>
    </div>
    <div className="col-span-1 text-right">
      <ChangeIndicator change={f.change} delta={f.delta} />
    </div>
  </motion.div>
);

const ChangeIndicator: React.FC<{ change: 'up' | 'down' | 'same'; delta: number }> = ({ change, delta }) => {
  if (change === 'up') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
        <ArrowUp className="w-3 h-3" /> {delta}
      </span>
    );
  }
  if (change === 'down') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono text-red-500">
        <ArrowDown className="w-3 h-3" /> {delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[11px] font-mono text-[var(--muted)]">
      <Minus className="w-3 h-3" />
    </span>
  );
};

const ReadersBoard: React.FC<{ readers: ReaderLeaderboardEntry[]; status: LoadStatus }> = ({ readers, status }) => {
  if (status === 'error' && readers.length === 0) {
    return <EmptyState icon={Crown} title="Couldn't load readers" body="The leaderboard failed to fetch. Refresh to try again." />;
  }
  if (readers.length === 0) {
    return <EmptyState icon={Crown} title="No readers yet" body="Top readers appear once enrolled students log their first reads." />;
  }
  const max = readers[0].books || 1;
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
      {readers.map((r, i) => {
        const isTop = r.rank <= 3;
        return (
          <motion.div
            key={`${r.display_name}-${r.rank}`}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
            className={`grid grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4 border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface-2)] transition-colors ${
              r.rank === 1 ? 'bg-[var(--gold)]/5' : ''
            }`}
          >
            <div className="col-span-1">
              {isTop ? (
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    r.rank === 1
	                    ? 'bg-[var(--gold)] text-[var(--ink)]'
	                    : r.rank === 2
	                      ? 'bg-[var(--challenge-medal-silver-bg)] text-[var(--ink-soft)]'
	                      : 'bg-[var(--challenge-medal-bronze-bg)] text-[var(--ink-soft)]'
	                  }`}
                >
                  {r.rank === 1 ? <Crown className="w-3.5 h-3.5" /> : <Medal className="w-3.5 h-3.5" />}
                </div>
              ) : (
                <span className="font-mono text-[14px] font-semibold text-[var(--muted)]">#{r.rank}</span>
              )}
            </div>
            <div className="col-span-4 md:col-span-3 min-w-0">
              <div className="font-display font-bold text-[1.05rem] tracking-tight truncate">
                {r.display_name}
              </div>
              <div className="text-[11px] text-[var(--muted)] font-mono uppercase tracking-wider">
                {r.year}
              </div>
            </div>
            <div className="col-span-3 md:col-span-2">
              {r.badge && (
                <span className="inline-block text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[var(--gold)]/15 text-[var(--ink-soft)]">
                  {r.badge}
                </span>
              )}
            </div>
            <div className="col-span-2 md:col-span-3 hidden md:block">
              <div className="h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: Math.min(1, r.books / max) }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.04 + 0.1, ease: 'easeOut' }}
                  className="h-full bg-[var(--primary)] origin-left rounded-full"
                />
              </div>
            </div>
            <div className="col-span-2 text-right text-[13px]">
              <span className="font-display font-bold text-[1.15rem]">{r.books}</span>{' '}
              <span className="text-[var(--muted)] text-[11px] font-mono uppercase tracking-wider">books</span>
            </div>
            <div className="col-span-2 md:col-span-1 text-right">
              <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--ink-soft)]">
                <Flame className="w-3 h-3 text-[var(--gold)]" /> {r.streak}d
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

/* =============================================================== */
/*  MILESTONES / BADGES                                             */
/* =============================================================== */

const Milestones: React.FC<{ milestones: Milestone[] }> = ({ milestones }) => {
  if (milestones.length === 0) return null;
  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-10">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <Kicker label="Personal milestones" />
          <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[24ch]">
            Five badges.{' '}
            Earn them
            all.
          </h2>
        </div>
        <p className="text-[13px] text-[var(--muted)] font-mono uppercase tracking-wider max-w-[34ch]">
          Badges live on your profile and the librarian's wall of fame.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {milestones.map((m, i) => {
          const Icon = MILESTONE_ICONS[m.threshold] ?? BookOpen;
          return (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              className="group rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5 hover:border-[var(--ink)]/30 hover:-translate-y-1 transition-all"
            >
              <div className="relative w-16 h-16 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full bg-[var(--gold)]/10 group-hover:bg-[var(--gold)]/20 transition-colors" />
                <div className="absolute inset-1.5 rounded-full bg-[var(--surface)] border border-[var(--gold)]/40 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[var(--ink)]" strokeWidth={1.6} />
                </div>
              </div>
              <div className="text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold-text)] font-semibold">
                  {m.threshold}+ books
                </div>
                <div className="font-display font-bold text-[1.15rem] tracking-tight mt-1">
                  {m.name}
                </div>
                <p className="mt-1.5 text-[12px] text-[var(--ink-soft)] leading-[1.45] font-serif italic">
                  "{m.desc}"
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

/* =============================================================== */
/*  HALL OF FAME                                                    */
/* =============================================================== */

const HallOfFame: React.FC<{ winners: ChallengeWinner[] }> = ({ winners }) => {
  if (winners.length === 0) return null;
  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-10">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <Kicker label="Hall of fame" />
          <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[22ch]">
            Past{' '}
            winners,
            permanent record.
          </h2>
        </div>
      </div>

      <div className="border-y border-[var(--border)]">
        {winners.map((w, i) => (
          <motion.div
            key={w.id}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            className="grid grid-cols-12 gap-4 md:gap-6 items-center py-6 md:py-7 border-b border-[var(--border)] last:border-0 group"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--gold-text)]">
                {w.term_label}
              </div>
            </div>
            <div className="col-span-6 md:col-span-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--muted)]">
                Winning form
              </div>
              <div className="font-display font-bold text-[1.5rem] md:text-[1.75rem] tracking-tight mt-1 inline-flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[var(--gold)]" /> {w.winning_form}
              </div>
              <div className="text-[12px] text-[var(--ink-soft)] mt-0.5">{w.form_total_books} books</div>
            </div>
            <div className="col-span-6 md:col-span-4">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--muted)]">
                Top reader
              </div>
              <div className="font-display font-bold text-[1.25rem] tracking-tight mt-1">
                {w.top_reader_name}
              </div>
              <div className="text-[12px] text-[var(--ink-soft)] mt-0.5">
                {w.top_reader_books} books read
              </div>
            </div>
            <div className="col-span-12 md:col-span-2 md:text-right">
              <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--ink-soft)] inline-flex items-center gap-1.5">
                {new Date(w.published_at).toLocaleDateString()}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* =============================================================== */
/*  JOIN CTA                                                        */
/* =============================================================== */

const JoinCTA: React.FC = () => (
  <section id="join" className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-24">
    <div className="relative rounded-3xl bg-[var(--ink)] text-[var(--paper)] p-10 md:p-16 overflow-hidden">
      <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border border-[var(--paper)]/10" />
      <div className="absolute -right-12 -bottom-12 w-72 h-72 rounded-full border border-[var(--paper)]/10" />
      <div className="absolute right-6 bottom-6 w-40 h-40 rounded-full bg-[var(--gold)]/20 blur-3xl" />

      <Trophy className="absolute top-8 right-8 w-32 h-32 text-[var(--gold)]/15" strokeWidth={0.8} />

      <div className="relative max-w-[36ch]">
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--paper)]/60 mb-5 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[var(--gold)]" /> Get started
        </div>
        <h2 className="font-display font-[900] text-[2.5rem] md:text-[3.75rem] leading-[0.98] tracking-[-0.03em]">
          Your first book counts.
        </h2>
        <p className="mt-6 text-[var(--paper)]/70 max-w-[52ch] text-[15px] leading-[1.55]">
          Sign in, opt into a challenge, and log your reads. The librarian
          will know, and the leaderboard updates within minutes.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="/signin"
            className="inline-flex items-center gap-2 bg-[var(--gold)] text-[var(--challenge-gold-button-ink)] px-5 py-3 rounded-xl font-semibold text-[14px] hover:brightness-110 transition-all shadow-[var(--challenge-cta-shadow)]"
          >
            Join the challenge <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="/catalog"
            className="inline-flex items-center gap-2 border border-[var(--paper)]/25 hover:border-[var(--paper)]/60 text-[var(--paper)] px-5 py-3 rounded-xl font-medium text-[14px] transition-colors"
          >
            Pick your first book
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* =============================================================== */
/*  LOG-A-READ MODAL                                                */
/* =============================================================== */

type LogMode = 'catalog' | 'manual';

const LogReadModal: React.FC<{
  open: boolean;
  preferCatalog: boolean;
  onClose: () => void;
  onLogged: () => void;
}> = ({ open, preferCatalog, onClose, onLogged }) => {
  const [mode, setMode] = useState<LogMode>('catalog');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [readDate, setReadDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<Book[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);

  useEffect(() => {
    if (open) {
      setMode(preferCatalog ? 'catalog' : 'manual');
      setTitle('');
      setAuthor('');
      setReadDate(todayIso());
      setNotes('');
      setSearch('');
      setDebounced('');
      setResults([]);
      setSelectedBook(null);
    }
  }, [open, preferCatalog]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open || mode !== 'catalog' || selectedBook) return;
    if (debounced.length < 2) { setResults([]); return; }
    setSearching(true);
    booksApi
      .getBooks({ search: debounced, page_size: 8 })
      .then((r) => setResults(r.results))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [open, mode, debounced, selectedBook]);

  if (!open) return null;

  async function submit() {
    if (mode === 'catalog') {
      if (!selectedBook) {
        toast.error('Pick a book from the catalog, or switch to manual entry.');
        return;
      }
    } else {
      if (!title.trim() || !author.trim()) {
        toast.error('Title and author are required.');
        return;
      }
    }
    setSaving(true);
    try {
      await challengesReadLogsApi.create(
        mode === 'catalog' && selectedBook
          ? {
              book: selectedBook.id,
              read_date: readDate,
              notes: notes.trim(),
            }
          : {
              title: title.trim(),
              author: author.trim(),
              read_date: readDate,
              notes: notes.trim(),
            },
      );
      toast.success('Read logged. Leaderboard will update shortly.');
      onLogged();
    } catch {
      /* interceptor toasts */
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl w-full max-w-md p-6 md:p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold-text)]">
              New entry
            </div>
            <h3 className="font-display font-bold text-[1.5rem] tracking-tight mt-1">
              Log a read
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-[13px] text-[var(--ink-soft)] mb-4 leading-[1.5]">
          Pick a book from the catalog so it counts toward{' '}
          <span className="font-medium">genre challenges</span> too. Manual entries
          still count for book-count challenges.
        </p>

        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[var(--surface-2)] border border-[var(--border)] mb-4">
          {(['catalog', 'manual'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-[12px] font-medium rounded transition-colors ${
                mode === m
                  ? 'bg-[var(--ink)] text-[var(--paper)]'
                  : 'text-[var(--ink-soft)] hover:text-[var(--ink)]'
              }`}
            >
              {m === 'catalog' ? 'Pick from catalog' : 'Type manually'}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {mode === 'catalog' ? (
            selectedBook ? (
              <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[14px] truncate">
                    {selectedBook.title}
                  </div>
                  <div className="text-[12px] text-[var(--ink-soft)] truncate">
                    {authorLine(selectedBook)}
                  </div>
                  {selectedBook.genres?.length > 0 && (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--muted)] mt-1 truncate">
                      {selectedBook.genres.map((g) => g.name).join(' · ')}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedBook(null)}
                  className="text-[var(--muted)] hover:text-[var(--ink)] shrink-0"
                  aria-label="Clear selection"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Field label="Find your book">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title or author…"
                  className="w-full h-10 px-3 bg-[var(--surface-2)] border border-[var(--border)] rounded-lg text-[14px] focus:outline-none focus:border-[var(--ink)]/40"
                />
                {debounced.length > 0 && debounced.length < 2 && (
                  <p className="mt-1.5 text-[11px] text-[var(--muted)]">Keep typing…</p>
                )}
                {searching && (
                  <p className="mt-1.5 text-[11px] text-[var(--muted)] inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Searching catalog…
                  </p>
                )}
                {!searching && debounced.length >= 2 && results.length === 0 && (
                  <p className="mt-1.5 text-[11px] text-[var(--muted)]">No books match. Try a different term, or switch to manual entry.</p>
                )}
                {results.length > 0 && (
                  <ul className="mt-2 max-h-56 overflow-auto rounded-lg border border-[var(--border)] divide-y divide-[var(--border)]">
                    {results.map((b) => (
                      <li key={b.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedBook(b)}
                          className="w-full text-left px-3 py-2 hover:bg-[var(--surface-2)] transition-colors"
                        >
                          <div className="font-display font-medium text-[13px] truncate">{b.title}</div>
                          <div className="text-[11px] text-[var(--ink-soft)] truncate">
                            {authorLine(b)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
            )
          ) : (
            <>
              <Field label="Title">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Book title"
                  maxLength={300}
                  className="w-full h-10 px-3 bg-[var(--surface-2)] border border-[var(--border)] rounded-lg text-[14px] focus:outline-none focus:border-[var(--ink)]/40"
                />
              </Field>
              <Field label="Author">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Author name"
                  maxLength={200}
                  className="w-full h-10 px-3 bg-[var(--surface-2)] border border-[var(--border)] rounded-lg text-[14px] focus:outline-none focus:border-[var(--ink)]/40"
                />
              </Field>
            </>
          )}
          <Field label="Date finished">
            <input
              type="date"
              value={readDate}
              min={minReadDateIso()}
              max={todayIso()}
              onChange={(e) => setReadDate(e.target.value)}
              className="w-full h-10 px-3 bg-[var(--surface-2)] border border-[var(--border)] rounded-lg text-[14px] focus:outline-none focus:border-[var(--ink)]/40"
            />
          </Field>
          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="A line or two about the book…"
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--border)] rounded-lg text-[14px] focus:outline-none focus:border-[var(--ink)]/40 resize-none"
            />
          </Field>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg border border-[var(--border)] text-[13px] font-medium hover:bg-[var(--surface-2)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="h-10 px-4 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2 transition-all"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Log read
          </button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] block mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

/* =============================================================== */
/*  Empty state                                                     */
/* =============================================================== */

const EmptyState: React.FC<{
  icon: React.ElementType;
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ icon: Icon, title, body, actionLabel, onAction }) => (
  <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--surface)] py-12 px-6 text-center">
    <Icon className="w-10 h-10 mx-auto text-[var(--muted)] mb-3" strokeWidth={1.5} />
    <div className="font-display font-bold text-[1.15rem] tracking-tight">{title}</div>
    <p className="mt-2 text-[13px] text-[var(--ink-soft)] max-w-[44ch] mx-auto leading-[1.5]">{body}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:brightness-110 transition-all"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export default ChallengesPage;
